import { Button } from '@otter-ai/den-components';
import { SlackIcon } from '@otter-ai/den-icons';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';
import { FormattedText } from '@/shared/components/FormattedText';
import { openConfirmDialog } from '@/shared/modals/confirm/openConfirmDialog';
import { BUILT_IN_APPS } from '@/shared/types/apps';
import { descriptionFormatRules } from '../../../shared/utils/text';
import { useGetUserSlackSharingConfigs } from '../../hooks/slack/useGetUserSlackSharingConfigs';
import { useSlackModal } from '../../hooks/slack/useSlackModal';
import { useSlackOAuth } from '../../hooks/slack/useSlackOAuth';
import { useSlackSettings } from '../../hooks/slack/useSlackSettings';
import { useCalendarConnection } from '../../hooks/useCalendarConnection';
import { useSyncedAccounts } from '../../hooks/useSyncedAccounts';
import { SLACK_TEST_IDS } from '../../test-ids/slack';
import { trackSlack } from '../../tracking/slack-tracking';
import type { SlackSyncedAccount } from '../../type/apps';
import { AppRowLayout } from '../AppRowLayout';
import { DisconnectedButton } from '../DisconnectedButton';
import { ConnectMeetingsToSlackCta } from './ConnectMeetingsToSlackCta';

/**
 * The row for Slack on the apps settings page.
 */
export function SlackAppRow() {
  const t = useTranslations('settings:integrations.apps.slack');
  const { openModal: openSlackModal } = useSlackModal();
  const { canAddSlackAccount, isConnectedToSlack } = useSlackSettings();
  const { isConnected: isCalendarConnected } = useCalendarConnection();
  const { openSlackAuthorizationPage, disconnectSlack } = useSlackOAuth();
  const { accounts } = useSyncedAccounts({
    service: BUILT_IN_APPS.SLACK,
  });
  const {
    userMeetingSlackSharingConfigs: slackSharingConfigs,
    isLoading: isLoadingSlackSharingConfigs,
  } = useGetUserSlackSharingConfigs();

  const handleAddClick = useCallback(() => {
    trackSlack.main.workspaceConnectionStart();
    openSlackAuthorizationPage();
  }, [openSlackAuthorizationPage]);

  const handleDisconnect = useCallback(
    async (account: SlackSyncedAccount) => {
      trackSlack.main.individualDisconnectStart();
      const confirmed = await openConfirmDialog({
        title: t('disconnectModal.title'),
        message: (
          <FormattedText>
            {t.rich('disconnectModal.description', descriptionFormatRules())}
          </FormattedText>
        ),
        confirmText: t('disconnectModal.confirmText'),
        cancelText: t('disconnectModal.cancelText'),
        danger: true,
      });
      if (!confirmed) {
        return true;
      }

      try {
        await disconnectSlack(account);
        // TODO(sleahy): Add success toast
        return true;
      } catch {
        // TODO(sleahy): Add error toast
        return false;
      }
    },
    [disconnectSlack],
  );

  const { openSlackWorkspaceInstallationPage } = useSlackOAuth();
  const {
    isWorkspaceInstallationCompleted,
    isLoading: isLoadingSlackSettings,
  } = useSlackSettings();

  return (
    <AppRowLayout
      appRowId="slack-app"
      tags={{ newTag: canAddSlackAccount ?? false }}
      Icon={SlackIcon}
      title="Slack"
      description={
        <FormattedText>
          {t.rich('description', descriptionFormatRules())}
        </FormattedText>
      }
      extendContent={
        <ConnectMeetingsToSlackCta
          isConnectedToSlack={Boolean(isConnectedToSlack)}
          isCalendarConnected={Boolean(isCalendarConnected)}
          openSlackModal={openSlackModal}
          userMeetingSlackSharingConfigs={slackSharingConfigs}
          isLoadingSlackSharingConfigs={isLoadingSlackSharingConfigs}
          openSlackWorkspaceInstallation={openSlackWorkspaceInstallationPage}
          isLoadingSlackSettings={isLoadingSlackSettings}
          isWorkspaceInstallationCompleted={isWorkspaceInstallationCompleted}
        />
      }
      buttons={
        <div className="flex gap-2 flex-col">
          {canAddSlackAccount && (
            <Button
              onClick={handleAddClick}
              data-testid={SLACK_TEST_IDS.addButton}
            >
              {t('addButton')}
            </Button>
          )}
        </div>
      }
      linkedAccounts={accounts.map((account) => ({
        accountName: t('accountName', {
          accountName: account.account,
          teamName: account.team_name,
        }),
        accountId: account.slack_user_id,
        button: (
          <DisconnectedButton
            disconnectButtonText={t('disconnectButton')}
            disconnectAction={() => handleDisconnect(account)}
            tooltipCaption={t('disconnectTooltip', {
              accountName: account.account,
            })}
            data-testid={SLACK_TEST_IDS.disconnectButton}
          />
        ),
      }))}
    />
  );
}
